/**
 * MECANISMO dos interruptores do app (BLUEPRINT §2, Fase 2). Aqui vive o que é
 * do MOLDE: os tipos das chaves, o mapa de rotas e os helpers (`isFeatureEnabled`
 * etc.). As ESCOLHAS do cliente (quais estão on/off) moram em
 * `src/cliente/features.ts` — importadas e re-exportadas abaixo.
 *
 * Por que separado: sincronizar um app de cliente com o molde sobrepõe
 * `src/lib` (mecanismo) e PRESERVA `src/cliente` (escolhas). Se os valores
 * vivessem aqui, um sync clobraria os on/off do cliente.
 *
 * O app inteiro importa DAQUI (`@/lib/features`) — o re-export mantém a API
 * pública inalterada. Não importe de `@/cliente/features` direto.
 */

import {
  CAIXA_AREAS,
  FEATURES,
  FINANCE_OPTIONS,
  FUNIL_OPTIONS,
  ODONTOGRAMA_OPTIONS,
  QUOTE_OPTIONS,
} from "@/shared/cliente/features";
import { isAllowedBySpecialty } from "@/shared/especialidades";

export { CAIXA_AREAS, FEATURES, FINANCE_OPTIONS, FUNIL_OPTIONS, ODONTOGRAMA_OPTIONS, QUOTE_OPTIONS };

/**
 * Uma área de caixa — um "bolso" que a clínica confere em separado. Ver
 * `CAIXA_AREAS` em `src/cliente/features.ts` e a opção `caixaPorArea`.
 */
export type CaixaArea = {
  /** Identificador curto que viaja na URL (?area=orto). */
  readonly key: string;
  /** Nome que aparece no filtro. */
  readonly label: string;
  /** Categorias do catálogo de procedimentos que caem nesta área. */
  readonly categorias: readonly string[];
};

/** Estilo do desenho do odontograma. Ver `ODONTOGRAMA_OPTIONS`. */
export type OdontogramaEstilo = "anatomico" | "quadrado";

export type FeatureKey = keyof typeof FEATURES;
export type FinanceOptionKey = keyof typeof FINANCE_OPTIONS;
export type FunilOptionKey = keyof typeof FUNIL_OPTIONS;
export type QuoteOptionKey = keyof typeof QUOTE_OPTIONS;

/**
 * Prefixos de rota dos MÓDULOS NAVEGÁVEIS — usado pelo middleware pra bloquear
 * acesso direto por URL quando o módulo está desligado. Capacidades sem aba
 * própria (ex.: `iaClinica`) NÃO entram aqui (checadas no ponto de uso) — por
 * isso o Partial. É MECANISMO (o molde define as rotas), não escolha do cliente.
 */
export const FEATURE_ROUTES: Partial<Record<FeatureKey, string>> = {
  cirurgias: "/app/cirurgias",
  comunicacao: "/app/comunicacao",
  financeiro: "/app/financeiro",
  mural: "/app/mural",
  minhasFinancas: "/app/minhas-financas",
  solicitacoes: "/app/solicitacoes",
  estoque: "/app/estoque",
  retornos: "/app/retornos",
  orcamentos: "/app/orcamentos",
  radar: "/app/radar",
  campanhas: "/app/campanhas",
  dossieDia: "/app/dossie",
  funil: "/app/funil",
};

/** Ligado no `features.ts` E (não é recurso de especialidade OU a especialidade permite). */
export function isFeatureEnabled(key: FeatureKey): boolean {
  return FEATURES[key] && isAllowedBySpecialty(key);
}

/**
 * Uma peça do Financeiro só está ligada se o MÓDULO estiver ligado. Assim,
 * `financeiro: false` desliga tudo de uma vez e nenhum card escapa.
 */
export function isFinanceOption(key: FinanceOptionKey): boolean {
  return FEATURES.financeiro && FINANCE_OPTIONS[key];
}

/**
 * Despesa por foto chama a Anthropic — exige a capacidade de IA clínica ligada
 * ALÉM da opção. Sempre use este helper (nunca `isFinanceOption("despesaPorFoto")`
 * sozinho), senão sobra chamada de IA num app vendido "sem IA".
 */
export function canUseExpensePhoto(): boolean {
  return isFinanceOption("despesaPorFoto") && FEATURES.iaClinica;
}

/**
 * Dossiê do dia chama a Anthropic — exige a IA clínica ligada ALÉM da
 * capacidade. Sempre use este helper (nunca `isFeatureEnabled("dossieDia")`
 * sozinho), senão sobra chamada de IA num app vendido "sem IA".
 */
export function canUseDossie(): boolean {
  return FEATURES.dossieDia && FEATURES.iaClinica;
}

/** Atalho legível pra capacidade multi-profissional. */
export function isMultiProfissional(): boolean {
  return FEATURES.multiProfissional;
}

/**
 * Uma peça do Funil só está ligada se o MÓDULO estiver ligado — assim
 * `funil: false` desliga tudo de uma vez.
 */
export function isFunilOption(key: FunilOptionKey): boolean {
  return FEATURES.funil && FUNIL_OPTIONS[key];
}

/**
 * Uma peça dos Orçamentos só está ligada se o MÓDULO estiver ligado — assim
 * `orcamentos: false` desliga tudo de uma vez.
 */
export function isQuoteOption(key: QuoteOptionKey): boolean {
  return FEATURES.orcamentos && QUOTE_OPTIONS[key];
}

/** Devolve a feature dona do pathname, ou null se a rota é do núcleo. */
export function featureForPath(pathname: string): FeatureKey | null {
  for (const key of Object.keys(FEATURE_ROUTES) as FeatureKey[]) {
    const prefix = FEATURE_ROUTES[key];
    if (!prefix) continue;
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return key;
  }
  return null;
}
